import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { api } from '../lib/api';
import { API_BASE_URL, ENDPOINTS } from '../config/constants';
import { Save, X, Upload, Trash2, Image, Check, AlertCircle, User, Video, Gift, ChevronDown } from 'lucide-react';
import { log } from 'console';

interface Character {
  id: string;
  name: string;
  isPremium: boolean;
  isEnable: boolean;
  isReviewModeCharacter: boolean;
  posterImageUrl?: string;
  profileImageUrl?: string;
  allImages?: string[];
  allVideos?: string[];
  greetingVideoUrl?: string;
  voiceId?: string;
}

interface CharacterGift {
  id: number;
  isAvailable: boolean;
  imageUrls: string[];
}

interface CharacterGiftResponse {
  id: number;
  name: string;
  price: number;
  isActive: boolean;
  imageUrl: string;
  characterGifts: CharacterGift[];
}

const CharacterEdit: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [character, setCharacter] = useState<Character | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [posterFile, setPosterFile] = useState<File | null>(null);
  const [profileFile, setProfileFile] = useState<File | null>(null);
  const [generalFiles, setGeneralFiles] = useState<File[]>([]);
  const [videoFiles, setVideoFiles] = useState<File[]>([]);
  const [greetingVideoFile, setGreetingVideoFile] = useState<File | null>(null);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);

  // Hediye state'leri
  const [gifts, setGifts] = useState<any[]>([]);
  const [selectedGiftId, setSelectedGiftId] = useState<number | null>(null);
  const [giftImages, setGiftImages] = useState<File[]>([]);
  const [giftLoading, setGiftLoading] = useState(false);
  const [giftError, setGiftError] = useState<string | null>(null);
  const [giftSuccess, setGiftSuccess] = useState<string | null>(null);
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [characterGifts, setCharacterGifts] = useState<CharacterGiftResponse[]>([]);
  const [characterGiftsLoading, setCharacterGiftsLoading] = useState(false);

  const posterInputRef = useRef<HTMLInputElement>(null);
  const profileInputRef = useRef<HTMLInputElement>(null);
  const generalInputRef = useRef<HTMLInputElement>(null);
  const videoInputRef = useRef<HTMLInputElement>(null);
  const greetingVideoInputRef = useRef<HTMLInputElement>(null);
  const giftImagesInputRef = useRef<HTMLInputElement>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    fetchCharacter();
    fetchGifts();
    if (id) {
      fetchCharacterGifts();
    }
  }, [id]);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setDropdownOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  const fetchCharacter = async () => {
    try {
      const data = await api.get(`/character/${id}`);
      // API'dan gelen elevenlabsVoiceId'yi voiceId olarak set et
      const mappedCharacter = {
        ...data,
        voiceId: data.elevenlabsVoiceId || data.voiceId || ''
      };
      setCharacter(mappedCharacter);
      console.log(mappedCharacter);
    } catch (err) {
      setError('Karakter yüklenirken bir hata oluştu');
    } finally {
      setLoading(false);
    }
  };

  const fetchGifts = async () => {
    try {
      const token = localStorage.getItem('access_token');
      const response = await fetch(`${API_BASE_URL}${ENDPOINTS.GIFTS}`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'ngrok-skip-browser-warning': 'true',
        },
      });

      if (response.ok) {
        const data = await response.json();
        setGifts(data);
      }
    } catch (err) {
      console.error('Hediyeler yüklenirken hata oluştu:', err);
    }
  };

  const fetchCharacterGifts = async () => {
    try {
      setCharacterGiftsLoading(true);
      const token = localStorage.getItem('access_token');
      const response = await fetch(`${API_BASE_URL}${ENDPOINTS.CHARACTER_GIFTS}/${id}`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'ngrok-skip-browser-warning': 'true',
        },
      });

      if (response.ok) {
        const data = await response.json();
        setCharacterGifts(data);
      }
    } catch (err) {
      console.error('Karakter hediyeleri yüklenirken hata oluştu:', err);
    } finally {
      setCharacterGiftsLoading(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!character) return;

    setLoading(true);
    setError(null);
    setSuccess(null);

    try {
      // Önce karakter bilgilerini güncelle
      console.log(character);
      
      const updateData = {
        characterId: parseInt(id || '0'),
        name: character.name || null,
        isPremium: character.isPremium,
        isEnable: character.isEnable,
        isReviewModeCharacter: character.isReviewModeCharacter,
        elevenlabsVoiceId: character.voiceId || null
      };

      const token = localStorage.getItem('access_token');
      const updateResponse = await fetch(`${API_BASE_URL}${ENDPOINTS.CHARACTER_UPDATE}`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
          'ngrok-skip-browser-warning': 'true',
        },
        body: JSON.stringify(updateData)
      });

      if (!updateResponse.ok) {
        const errorData = await updateResponse.json().catch(() => ({}));
        throw new Error(`Karakter bilgileri güncellenemedi: ${errorData.message || updateResponse.statusText}`);
      }

      // Resimleri yükle
      if (posterFile || profileFile || generalFiles.length > 0) {
        const formData = new FormData();
        formData.append('characterId', id || '');

        if (posterFile) {
          formData.append('posterImage', posterFile);
        }
        if (profileFile) {
          formData.append('profileImage', profileFile);
        }
        generalFiles.forEach((file, index) => {
          formData.append(`generalImage`, file);
        });

        await fetch(`${API_BASE_URL}/dev/website/update/character-images`, {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('access_token')}`,
          },
          body: formData
        });
      }

      // Videoları yükle
      if (videoFiles.length > 0) {
        const videoFormData = new FormData();
        videoFormData.append('characterId', id || '');

        videoFiles.forEach((file) => {
          videoFormData.append('characterVideos', file);
        });

        const videoResponse = await fetch(`${API_BASE_URL}/dev/website/update/character-videos`, {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('access_token')}`,
            'ngrok-skip-browser-warning': 'true',
          },
          body: videoFormData
        });
        
        if (!videoResponse.ok) {
          const errorData = await videoResponse.json().catch(() => ({}));
          throw new Error(`Video yükleme başarısız: ${errorData.message || videoResponse.statusText}`);
        }
      }

      // Başarılı yüklemeden sonra state'leri temizle
      setPosterFile(null);
      setProfileFile(null);
      setGeneralFiles([]);
      setVideoFiles([]);
      setGreetingVideoFile(null);
      
      // Character'ı yeniden fetch et
      await fetchCharacter();

      setSuccess('Karakter başarıyla güncellendi');
      setTimeout(() => {
        setSuccess(null);
      }, 3000);
      
    } catch (err) {
      setError('Karakter güncellenirken bir hata oluştu');
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async () => {
    if (!id) return;

    setLoading(true);
    setError(null);
    setSuccess(null);

    try {
      await api.post(`/character/${id}/delete`, {});
      setSuccess('Karakter başarıyla silindi');
      setTimeout(() => {
        navigate('/');
      }, 1500);
    } catch (err) {
      setError('Karakter silinirken bir hata oluştu');
    } finally {
      setLoading(false);
      setShowDeleteConfirm(false);
    }
  };

  const handleImageChange = (e: React.ChangeEvent<HTMLInputElement>, type: 'poster' | 'profile' | 'general' | 'video' | 'greeting') => {
    const files = e.target.files;
    if (!files) return;

    if (type === 'poster') {
      setPosterFile(files[0]);
      if (posterInputRef.current) {
        posterInputRef.current.value = '';
      }
    } else if (type === 'profile') {
      setProfileFile(files[0]);
      if (profileInputRef.current) {
        profileInputRef.current.value = '';
      }
    } else if (type === 'general') {
      setGeneralFiles(Array.from(files));
      if (generalInputRef.current) {
        generalInputRef.current.value = '';
      }
    } else if (type === 'video') {
      setVideoFiles(Array.from(files));
      if (videoInputRef.current) {
        videoInputRef.current.value = '';
      }
    } else if (type === 'greeting') {
      setGreetingVideoFile(files[0]);
      if (greetingVideoInputRef.current) {
        greetingVideoInputRef.current.value = '';
      }
    }
  };

  const removeImage = (type: 'poster' | 'profile' | 'general' | 'video' | 'greeting', index?: number) => {
    if (type === 'poster') {
      setPosterFile(null);
    } else if (type === 'profile') {
      setProfileFile(null);
    } else if (type === 'general' && index !== undefined) {
      setGeneralFiles(prev => prev.filter((_, i) => i !== index));
    } else if (type === 'video' && index !== undefined) {
      setVideoFiles(prev => prev.filter((_, i) => i !== index));
    } else if (type === 'greeting') {
      setGreetingVideoFile(null);
    }
  };

  const deleteVideo = async (videoPath: string, index: number) => {
    try {
      setLoading(true);
      setError(null);
      
      // URL'den S3 key'ini çıkar
      // videoPath örneği: "https://bucket.s3.amazonaws.com/characters/1/videos/video.mp4"
      // veya sadece: "/characters/1/videos/video.mp4"
      let videoS3Key = videoPath;
      
      // Eğer tam URL ise, sadece path kısmını al
      if (videoPath.startsWith('http')) {
        try {
          const url = new URL(videoPath);
          videoS3Key = url.pathname.startsWith('/') ? url.pathname.substring(1) : url.pathname;
        } catch (e) {
          // URL parsing başarısız olursa, API_BASE_URL'i kaldırmayı dene
          videoS3Key = videoPath.replace(API_BASE_URL, '').replace(/^\//, '');
        }
      } else if (videoPath.startsWith('/')) {
        // Eğer '/' ile başlıyorsa, onu kaldır
        videoS3Key = videoPath.substring(1);
      }
      
      const token = localStorage.getItem('access_token');
      const response = await fetch(`${API_BASE_URL}${ENDPOINTS.CHARACTER_DELETE_VIDEO}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
          'ngrok-skip-browser-warning': 'true',
        },
        body: JSON.stringify({
          characterId: parseInt(id || '0'),
          videoS3Key: videoS3Key
        }),
      });

      if (response.ok) {
        // Character'ı yeniden fetch et
        await fetchCharacter();
        setSuccess('Video başarıyla silindi');
        setTimeout(() => {
          setSuccess(null);
        }, 3000);
      } else {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.message || 'Video silinirken hata oluştu');
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Video silinirken hata oluştu');
    } finally {
      setLoading(false);
    }
  };

  const uploadGreetingVideo = async () => {
    if (!greetingVideoFile || !character?.id) {
      setError('Selamlama videosu ve karakter ID gerekli');
      return;
    }

    try {
      setLoading(true);
      setError(null);
      
      const formData = new FormData();
      formData.append('characterId', character.id);
      formData.append('greetingVideo', greetingVideoFile);

      const token = localStorage.getItem('access_token');
      const response = await fetch(`${API_BASE_URL}${ENDPOINTS.CHARACTER_UPLOAD_GREETING_VIDEO}`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'ngrok-skip-browser-warning': 'true',
        },
        body: formData,
      });

      if (response.ok) {
        setGreetingVideoFile(null);
        await fetchCharacter();
        setSuccess('Selamlama videosu başarıyla yüklendi');
        setTimeout(() => {
          setSuccess(null);
        }, 3000);
      } else {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.message || 'Selamlama videosu yüklenirken hata oluştu');
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Selamlama videosu yüklenirken hata oluştu');
    } finally {
      setLoading(false);
    }
  };

  const deleteGreetingVideo = async () => {
    if (!character?.greetingVideoUrl || !character?.id) {
      setError('Silinecek selamlama videosu bulunamadı');
      return;
    }

    try {
      setLoading(true);
      setError(null);
      
      let videoS3Key = character.greetingVideoUrl;
      
      if (character.greetingVideoUrl.startsWith('http')) {
        try {
          const url = new URL(character.greetingVideoUrl);
          videoS3Key = url.pathname.startsWith('/') ? url.pathname.substring(1) : url.pathname;
        } catch (e) {
          videoS3Key = character.greetingVideoUrl.replace(API_BASE_URL, '').replace(/^\//, '');
        }
      } else if (character.greetingVideoUrl.startsWith('/')) {
        videoS3Key = character.greetingVideoUrl.substring(1);
      }
      
      const token = localStorage.getItem('access_token');
      const response = await fetch(`${API_BASE_URL}${ENDPOINTS.CHARACTER_DELETE_GREETING_VIDEO}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
          'ngrok-skip-browser-warning': 'true',
        },
        body: JSON.stringify({
          characterId: parseInt(character.id),
          greetingVideoS3Key: videoS3Key
        }),
      });

      if (response.ok) {
        await fetchCharacter();
        setSuccess('Selamlama videosu başarıyla silindi');
        setTimeout(() => {
          setSuccess(null);
        }, 3000);
      } else {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.message || 'Selamlama videosu silinirken hata oluştu');
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Selamlama videosu silinirken hata oluştu');
    } finally {
      setLoading(false);
    }
  };

  const deleteImage = async (imagePath: string, index: number) => {
    try {
      setLoading(true);
      setError(null);
      
      // URL'den S3 key'ini çıkar
      let imageS3Key = imagePath;
      
      // Eğer tam URL ise, sadece path kısmını al
      if (imagePath.startsWith('http')) {
        try {
          const url = new URL(imagePath);
          imageS3Key = url.pathname.startsWith('/') ? url.pathname.substring(1) : url.pathname;
        } catch (e) {
          // URL parsing başarısız olursa, API_BASE_URL'i kaldırmayı dene
          imageS3Key = imagePath.replace(API_BASE_URL, '').replace(/^\//, '');
        }
      } else if (imagePath.startsWith('/')) {
        // Eğer '/' ile başlıyorsa, onu kaldır
        imageS3Key = imagePath.substring(1);
      }
      
      const token = localStorage.getItem('access_token');
      const response = await fetch(`${API_BASE_URL}${ENDPOINTS.CHARACTER_DELETE_IMAGE}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
          'ngrok-skip-browser-warning': 'true',
        },
        body: JSON.stringify({
          characterId: parseInt(id || '0'),
          imageS3Key: imageS3Key
        }),
      });

      if (response.ok) {
        // Character'ı yeniden fetch et
        await fetchCharacter();
        setSuccess('Resim başarıyla silindi');
        setTimeout(() => {
          setSuccess(null);
        }, 3000);
      } else {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.message || 'Resim silinirken hata oluştu');
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Resim silinirken hata oluştu');
    } finally {
      setLoading(false);
    }
  };

  const deleteGiftImage = async (characterGiftId: number, imageUrl: string) => {
    try {
      setLoading(true);
      setError(null);
      
      // URL'den S3 key'ini çıkar
      let imageS3Key = imageUrl;
      
      // Eğer tam URL ise, sadece path kısmını al
      if (imageUrl.startsWith('http')) {
        try {
          const url = new URL(imageUrl);
          imageS3Key = url.pathname.startsWith('/') ? url.pathname.substring(1) : url.pathname;
        } catch (e) {
          // URL parsing başarısız olursa, API_BASE_URL'i kaldırmayı dene
          imageS3Key = imageUrl.replace(API_BASE_URL, '').replace(/^\//, '');
        }
      } else if (imageUrl.startsWith('/')) {
        // Eğer '/' ile başlıyorsa, onu kaldır
        imageS3Key = imageUrl.substring(1);
      }
      
      const token = localStorage.getItem('access_token');
      const response = await fetch(`${API_BASE_URL}${ENDPOINTS.CHARACTER_DELETE_GIFT_IMAGE}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
          'ngrok-skip-browser-warning': 'true',
        },
        body: JSON.stringify({
          characterGiftId: characterGiftId,
          imageS3Key: imageS3Key
        }),
      });

      if (response.ok) {
        // Hediye listesini yeniden fetch et
        await fetchCharacterGifts();
        setSuccess('Hediye resmi başarıyla silindi');
        setTimeout(() => {
          setSuccess(null);
        }, 3000);
      } else {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.message || 'Hediye resmi silinirken hata oluştu');
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Hediye resmi silinirken hata oluştu');
    } finally {
      setLoading(false);
    }
  };

  const updateGiftImages = async (characterGiftId: number, newImages: File[]) => {
    try {
      setLoading(true);
      setError(null);
      
      if (newImages.length === 0) {
        setError('Lütfen en az bir resim seçin');
        return;
      }

      const formData = new FormData();
      formData.append('characterGiftId', characterGiftId.toString());
      
      newImages.forEach((image) => {
        formData.append('images', image);
      });

      const token = localStorage.getItem('access_token');
      const response = await fetch(`${API_BASE_URL}${ENDPOINTS.CHARACTER_UPDATE_GIFT_IMAGES}`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'ngrok-skip-browser-warning': 'true',
        },
        body: formData,
      });

      if (response.ok) {
        await fetchCharacterGifts();
        setSuccess('Hediye resimleri başarıyla güncellendi');
        setTimeout(() => {
          setSuccess(null);
        }, 3000);
      } else {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.message || 'Hediye resimleri güncellenirken hata oluştu');
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Hediye resimleri güncellenirken hata oluştu');
    } finally {
      setLoading(false);
    }
  };

  const handleGiftImagesChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (!files) return;

    setGiftImages(Array.from(files));
    if (giftImagesInputRef.current) {
      giftImagesInputRef.current.value = '';
    }
  };

  const removeGiftImage = (index: number) => {
    setGiftImages(prev => prev.filter((_, i) => i !== index));
  };

  const handleGiftSelect = (gift: any) => {
    // Eğer bu hediye tipi zaten karakterde mevcutsa uyarı ver
    const existingGiftIds = characterGifts.map(g => g.id);
    if (existingGiftIds.includes(gift.id)) {
      setGiftError('Bu hediye tipi zaten mevcut');
      setTimeout(() => {
        setGiftError(null);
      }, 3000);
      return;
    }
    
    setSelectedGiftId(gift.id);
    setDropdownOpen(false);
    setGiftError(null); // Başarılı seçimde error'u temizle
  };

  const getSelectedGift = () => {
    return gifts.find(gift => gift.id === selectedGiftId);
  };

  const getAvailableGifts = () => {
    // Karakterin mevcut hediye tiplerinin ID'lerini çıkar
    const existingGiftIds = characterGifts.map(gift => gift.id);
    
    // Sadece karakterin sahip olmadığı hediye tiplerini döndür
    return gifts.filter(gift => !existingGiftIds.includes(gift.id));
  };

  const handleGiftSubmit = async () => {
    if (!selectedGiftId || giftImages.length === 0) {
      setGiftError('Lütfen bir hediye tipi seçin ve resim ekleyin');
      return;
    }

    if (!character?.id) {
      setGiftError('Karakter ID\'si bulunamadı');
      return;
    }

    setGiftLoading(true);
    setGiftError(null);
    setGiftSuccess(null);

    try {
      const token = localStorage.getItem('access_token');
      const formData = new FormData();
      
      formData.append('characterId', character.id);
      formData.append('giftId', selectedGiftId.toString());
      
      // Resimler images array'i olarak gönderilecek
      giftImages.forEach((file) => {
        formData.append('images', file);
      });

      // Debug: FormData içeriğini kontrol et
      console.log('FormData içeriği:');
      console.log('characterId:', character.id);
      console.log('giftId:', selectedGiftId);
      console.log('images count:', giftImages.length);
      giftImages.forEach((file, index) => {
        console.log(`image[${index}]:`, file.name, file.size);
      });

      const response = await fetch(`${API_BASE_URL}${ENDPOINTS.CHARACTER_GIFT_CREATE}`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'ngrok-skip-browser-warning': 'true',
        },
        body: formData,
      });

      if (response.ok) {
        setGiftSuccess('Hediye başarıyla eklendi!');
        setSelectedGiftId(null);
        setGiftImages([]);
        fetchCharacterGifts();
        setTimeout(() => {
          setGiftSuccess(null);
        }, 3000);
      } else {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.message || 'Hediye eklenirken hata oluştu');
      }
    } catch (err) {
      setGiftError(err instanceof Error ? err.message : 'Bir hata oluştu');
    } finally {
      setGiftLoading(false);
    }
  };

  const getImageUrl = (path: string | undefined) => {
    if (!path) return '';
    if (path.startsWith('http')) return path;
    return `${API_BASE_URL}${path}`;
  };

  if (loading) return (
    <div className="flex justify-center items-center h-64">
      <div className="animate-spin rounded-full h-16 w-16 border-t-2 border-b-2 border-blue-500"></div>
    </div>
  );

  if (error) return (
    <div className="bg-red-50 p-6 rounded-lg mb-6 flex items-center">
      <AlertCircle className="text-red-500 mr-3" size={24} />
      <p className="text-red-600 font-medium">{error}</p>
    </div>
  );

  return (
    <div className="max-w-4xl mx-auto bg-white rounded-xl shadow-sm overflow-hidden">
      <div className="bg-gradient-to-r from-emerald-600 to-teal-700 py-6 px-8">
        <div className="flex justify-between items-center">
          <h2 className="text-2xl font-bold text-white flex items-center">
            <div className="mr-3 bg-white bg-opacity-20 p-2 rounded-full">
              <User className="text-white" size={20} />
            </div>
            Karakter Düzenle
          </h2>
          <button
            onClick={() => setShowDeleteConfirm(true)}
            className="flex items-center px-4 py-2 bg-red-500 text-white rounded-lg hover:bg-red-600 transition"
          >
            <Trash2 size={18} className="mr-2" />
            Sil
          </button>
        </div>
      </div>

      <form onSubmit={handleSubmit} className="p-8">
        {/* Alerts */}
        {error && (
          <div className="mb-6 bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg flex items-center">
            <AlertCircle className="mr-2" size={16} />
            <span>{error}</span>
          </div>
        )}
        
        {success && (
          <div className="mb-6 bg-green-50 border border-green-200 text-green-700 px-4 py-3 rounded-lg flex items-center">
            <Check className="mr-2" size={16} />
            <span>{success}</span>
          </div>
        )}

        <div className="space-y-8">
          {/* Basic Info */}
          <div className="space-y-6">
            <h3 className="text-lg font-semibold text-gray-900">Temel Bilgiler</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="space-y-2">
                <label className="block text-sm font-medium text-gray-700">Karakter Adı</label>
                <div className="relative">
                  <input
                    type="text"
                    value={character?.name || ''}
                    onChange={(e) => setCharacter(prev => prev ? { ...prev, name: e.target.value } : null)}
                    className="block w-full px-4 py-3 bg-white border border-gray-300 rounded-lg shadow-sm focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 transition-colors duration-200"
                    placeholder="Karakter adını girin"
                  />
                </div>
              </div>
              <div className="space-y-2">
                <label className="block text-sm font-medium text-gray-700">Voice ID</label>
                <div className="relative">
                  <input
                    type="text"
                    value={character?.voiceId || ''}
                    onChange={(e) => setCharacter(prev => prev ? { ...prev, voiceId: e.target.value } : null)}
                    className="block w-full px-4 py-3 bg-white border border-gray-300 rounded-lg shadow-sm focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 transition-colors duration-200"
                    placeholder="ElevenLabs Voice ID"
                  />
                </div>
              </div>
            </div>
            <div className="flex flex-wrap items-center gap-6 pt-2">
              <label className="flex items-center space-x-3 cursor-pointer">
                <div className="relative">
                  <input
                    type="checkbox"
                    checked={character?.isPremium || false}
                    onChange={(e) => setCharacter(prev => prev ? { ...prev, isPremium: e.target.checked } : null)}
                    className="sr-only peer"
                  />
                  <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-emerald-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-emerald-600"></div>
                </div>
                <span className="text-sm font-medium text-gray-700">Premium</span>
              </label>
              <label className="flex items-center space-x-3 cursor-pointer">
                <div className="relative">
                  <input
                    type="checkbox"
                    checked={character?.isEnable || false}
                    onChange={(e) => setCharacter(prev => prev ? { ...prev, isEnable: e.target.checked } : null)}
                    className="sr-only peer"
                  />
                  <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-emerald-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-emerald-600"></div>
                </div>
                <span className="text-sm font-medium text-gray-700">Aktif</span>
              </label>
              <label className="flex items-center space-x-3 cursor-pointer">
                <div className="relative">
                  <input
                    type="checkbox"
                    checked={character?.isReviewModeCharacter || false}
                    onChange={(e) => setCharacter(prev => prev ? { ...prev, isReviewModeCharacter: e.target.checked } : null)}
                    className="sr-only peer"
                  />
                  <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-emerald-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-emerald-600"></div>
                </div>
                <span className="text-sm font-medium text-gray-700">Review Mode</span>
              </label>
            </div>
          </div>

          {/* Images */}
          <div className="space-y-4">
            <h3 className="text-lg font-semibold text-gray-900">Resimler</h3>
            
            {/* Poster Image */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Poster Resmi</label>
              <div className="flex items-center space-x-4">
                <div className="relative w-32 h-48 border-2 border-dashed border-gray-300 rounded-lg overflow-hidden">
                  {(posterFile || character?.posterImageUrl) ? (
                    <img
                      src={posterFile ? URL.createObjectURL(posterFile) : getImageUrl(character?.posterImageUrl)}
                      alt="Poster"
                      className="w-full h-full object-contain"
                    />
                  ) : (
                    <div className="w-full h-full flex items-center justify-center bg-gray-50">
                      <Image className="text-gray-400" size={24} />
                    </div>
                  )}
                </div>
                <div className="space-y-2">
                  <input
                    type="file"
                    ref={posterInputRef}
                    onChange={(e) => handleImageChange(e, 'poster')}
                    accept="image/*"
                    className="hidden"
                  />
                  <button
                    type="button"
                    onClick={() => posterInputRef.current?.click()}
                    className="flex items-center px-4 py-2 bg-white border border-gray-300 rounded-md text-sm font-medium text-gray-700 hover:bg-gray-50"
                  >
                    <Upload className="mr-2" size={16} />
                    Yeni Poster Yükle
                  </button>
                  {(posterFile || character?.posterImageUrl) && (
                    <button
                      type="button"
                      onClick={() => removeImage('poster')}
                      className="flex items-center px-4 py-2 bg-red-50 border border-red-200 rounded-md text-sm font-medium text-red-700 hover:bg-red-100"
                    >
                      <Trash2 className="mr-2" size={16} />
                      Poster'ı Kaldır
                    </button>
                  )}
                </div>
              </div>
            </div>

            {/* Profile Image */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Profil Resmi</label>
              <div className="flex items-center space-x-4">
                <div className="relative w-32 h-32 border-2 border-dashed border-gray-300 rounded-lg overflow-hidden">
                  {(profileFile || character?.profileImageUrl) ? (
                    <img
                      src={profileFile ? URL.createObjectURL(profileFile) : getImageUrl(character?.profileImageUrl)}
                      alt="Profile"
                      className="w-full h-full object-contain"
                    />
                  ) : (
                    <div className="w-full h-full flex items-center justify-center bg-gray-50">
                      <Image className="text-gray-400" size={24} />
                    </div>
                  )}
                </div>
                <div className="space-y-2">
                  <input
                    type="file"
                    ref={profileInputRef}
                    onChange={(e) => handleImageChange(e, 'profile')}
                    accept="image/*"
                    className="hidden"
                  />
                  <button
                    type="button"
                    onClick={() => profileInputRef.current?.click()}
                    className="flex items-center px-4 py-2 bg-white border border-gray-300 rounded-md text-sm font-medium text-gray-700 hover:bg-gray-50"
                  >
                    <Upload className="mr-2" size={16} />
                    Yeni Profil Resmi Yükle
                  </button>
                  {(profileFile || character?.profileImageUrl) && (
                    <button
                      type="button"
                      onClick={() => removeImage('profile')}
                      className="flex items-center px-4 py-2 bg-red-50 border border-red-200 rounded-md text-sm font-medium text-red-700 hover:bg-red-100"
                    >
                      <Trash2 className="mr-2" size={16} />
                      Profil Resmini Kaldır
                    </button>
                  )}
                </div>
              </div>
            </div>

            {/* General Images */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Genel Resimler</label>
              <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
                {character?.allImages?.map((image, index) => (
                  <div key={index} className="relative group">
                    <div className="w-full h-48 bg-gray-100 border-2 border-gray-200 rounded-lg overflow-hidden shadow-sm hover:shadow-md transition-shadow">
                      <img
                        src={getImageUrl(image)}
                        alt={`General ${index + 1}`}
                        className="w-full h-full object-contain p-2"
                      />
                    </div>
                    <button
                      type="button"
                      onClick={() => deleteImage(image, index)}
                      className="absolute top-2 right-2 p-1 bg-red-500 text-white rounded-full opacity-0 group-hover:opacity-100 transition-opacity"
                    >
                      <Trash2 size={16} />
                    </button>
                  </div>
                ))}
                <div className="relative w-full h-48 border-2 border-dashed border-gray-300 rounded-lg overflow-hidden">
                  <input
                    type="file"
                    ref={generalInputRef}
                    onChange={(e) => handleImageChange(e, 'general')}
                    accept="image/*"
                    multiple
                    className="hidden"
                  />
                  <button
                    type="button"
                    onClick={() => generalInputRef.current?.click()}
                    className="w-full h-full flex flex-col items-center justify-center bg-gray-50 hover:bg-gray-100 transition-colors"
                  >
                    <Upload className="text-gray-400 mb-2" size={24} />
                    <span className="text-sm text-gray-600">Resim Ekle</span>
                  </button>
                </div>
              </div>
              {generalFiles.length > 0 && (
                <div className="mt-4 grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
                  {generalFiles.map((file, index) => (
                    <div key={index} className="relative group">
                      <div className="w-full h-48 bg-gray-100 border-2 border-gray-200 rounded-lg overflow-hidden shadow-sm hover:shadow-md transition-shadow">
                        <img
                          src={URL.createObjectURL(file)}
                          alt={`New ${index + 1}`}
                          className="w-full h-full object-contain p-2"
                        />
                      </div>
                      <button
                        type="button"
                        onClick={() => removeImage('general', index)}
                        className="absolute top-2 right-2 p-1 bg-red-500 text-white rounded-full opacity-0 group-hover:opacity-100 transition-opacity"
                      >
                        <Trash2 size={16} />
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Videos */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Videolar</label>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {character?.allVideos?.map((video, index) => (
                  <div key={index} className="relative group">
                    <div className="w-full h-48 bg-gray-100 border-2 border-gray-200 rounded-lg overflow-hidden shadow-sm hover:shadow-md transition-shadow">
                      <video
                        src={getImageUrl(video)}
                        controls
                        className="w-full h-full object-contain"
                      />
                    </div>
                    <button
                      type="button"
                      onClick={() => deleteVideo(video, index)}
                      className="absolute top-2 right-2 p-1 bg-red-500 text-white rounded-full opacity-0 group-hover:opacity-100 transition-opacity"
                    >
                      <Trash2 size={16} />
                    </button>
                  </div>
                ))}
                <div className="relative w-full h-48 border-2 border-dashed border-gray-300 rounded-lg overflow-hidden">
                  <input
                    type="file"
                    ref={videoInputRef}
                    onChange={(e) => handleImageChange(e, 'video')}
                    accept="video/*"
                    multiple
                    className="hidden"
                  />
                  <button
                    type="button"
                    onClick={() => videoInputRef.current?.click()}
                    className="w-full h-full flex flex-col items-center justify-center bg-gray-50 hover:bg-gray-100 transition-colors"
                  >
                    <Video className="text-gray-400 mb-2" size={24} />
                    <span className="text-sm text-gray-600">Video Ekle</span>
                  </button>
                </div>
              </div>
              {videoFiles.length > 0 && (
                <div className="mt-4 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                  {videoFiles.map((file, index) => (
                    <div key={index} className="relative group">
                      <div className="w-full h-48 bg-gray-100 border-2 border-gray-200 rounded-lg overflow-hidden shadow-sm hover:shadow-md transition-shadow">
                        <video
                          src={URL.createObjectURL(file)}
                          controls
                          className="w-full h-full object-contain"
                        />
                      </div>
                      <button
                        type="button"
                        onClick={() => removeImage('video', index)}
                        className="absolute top-2 right-2 p-1 bg-red-500 text-white rounded-full opacity-0 group-hover:opacity-100 transition-opacity"
                      >
                        <Trash2 size={16} />
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Selamlama Videosu */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Karakter Selamlama Videosu</label>
              
              {/* Mevcut Selamlama Videosu */}
              {character?.greetingVideoUrl && (
                <div className="mb-4">
                  <label className="block text-xs font-medium text-gray-500 mb-2">Mevcut Video</label>
                  <div className="relative group inline-block">
                    <div className="w-64 h-48 bg-gray-100 border-2 border-gray-200 rounded-lg overflow-hidden shadow-sm hover:shadow-md transition-shadow">
                      <video
                        src={getImageUrl(character.greetingVideoUrl)}
                        controls
                        className="w-full h-full object-contain"
                      />
                    </div>
                    <button
                      type="button"
                      onClick={deleteGreetingVideo}
                      className="absolute top-2 right-2 p-1 bg-red-500 text-white rounded-full opacity-0 group-hover:opacity-100 transition-opacity"
                    >
                      <Trash2 size={16} />
                    </button>
                  </div>
                </div>
              )}

              {/* Yeni Selamlama Videosu Yükleme */}
              <div className="space-y-3">
                <label className="block text-xs font-medium text-gray-500">
                  {character?.greetingVideoUrl ? 'Yeni Selamlama Videosu' : 'Selamlama Videosu Ekle'}
                </label>
                
                {greetingVideoFile && (
                  <div className="mb-3">
                    <div className="relative group inline-block">
                      <div className="w-64 h-48 bg-gray-100 border-2 border-gray-200 rounded-lg overflow-hidden shadow-sm">
                        <video
                          src={URL.createObjectURL(greetingVideoFile)}
                          controls
                          className="w-full h-full object-contain"
                        />
                      </div>
                      <button
                        type="button"
                        onClick={() => removeImage('greeting')}
                        className="absolute top-2 right-2 p-1 bg-red-500 text-white rounded-full opacity-0 group-hover:opacity-100 transition-opacity"
                      >
                        <Trash2 size={16} />
                      </button>
                    </div>
                  </div>
                )}

                <div className="flex items-center space-x-3">
                  <div className="relative w-48 h-32 border-2 border-dashed border-gray-300 rounded-lg overflow-hidden">
                    <input
                      type="file"
                      ref={greetingVideoInputRef}
                      onChange={(e) => handleImageChange(e, 'greeting')}
                      accept="video/*"
                      className="hidden"
                    />
                    <button
                      type="button"
                      onClick={() => greetingVideoInputRef.current?.click()}
                      className="w-full h-full flex flex-col items-center justify-center bg-gray-50 hover:bg-gray-100 transition-colors"
                    >
                      <Video className="text-gray-400 mb-2" size={20} />
                      <span className="text-xs text-gray-600">Selamlama Videosu Seç</span>
                    </button>
                  </div>
                  
                  {greetingVideoFile && (
                    <button
                      type="button"
                      onClick={uploadGreetingVideo}
                      disabled={loading}
                      className="flex items-center px-4 py-2 bg-purple-600 text-white rounded-md text-sm font-medium hover:bg-purple-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                    >
                      {loading ? (
                        <>
                          <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin mr-2"></div>
                          Yükleniyor...
                        </>
                      ) : (
                        <>
                          <Upload className="mr-2" size={16} />
                          Videoyu Yükle
                        </>
                      )}
                    </button>
                  )}
                </div>
              </div>
            </div>

            {/* Karakter Hediyeleri */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-4">Karakter Hediyeleri</label>
              
              {/* Success/Error Messages */}
              {giftSuccess && (
                <div className="mb-4 bg-green-50 border border-green-200 text-green-700 px-4 py-3 rounded-lg flex items-center">
                  <Check className="mr-2" size={16} />
                  {giftSuccess}
                </div>
              )}
              
              {giftError && (
                <div className="mb-4 bg-red-50 border border-red-200 text-red-600 px-4 py-3 rounded-lg flex items-center">
                  <AlertCircle className="mr-2" size={16} />
                  {giftError}
                </div>
              )}

              <div className="bg-gray-50 p-6 rounded-lg space-y-4">
                {/* Hediye Tipi Seçimi */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Hediye Tipi Seç
                  </label>
                  <div className="relative" ref={dropdownRef}>
                    <button
                      type="button"
                      onClick={() => setDropdownOpen(!dropdownOpen)}
                      className="w-full px-3 py-2 bg-white border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:border-transparent flex items-center justify-between"
                    >
                      {getSelectedGift() ? (
                        <div className="flex items-center space-x-3">
                          <img
                            src={getSelectedGift()?.imageUrl}
                            alt={getSelectedGift()?.name}
                            className="w-8 h-8 rounded-md object-cover"
                          />
                          <span className="text-gray-900">
                            {getSelectedGift()?.name} - {getSelectedGift()?.price}
                          </span>
                        </div>
                      ) : (
                        <span className="text-gray-500">Hediye tipi seçin...</span>
                      )}
                      <ChevronDown 
                        size={16} 
                        className={`text-gray-400 transition-transform ${dropdownOpen ? 'rotate-180' : ''}`}
                      />
                    </button>

                    {dropdownOpen && (
                      <div className="absolute z-10 w-full mt-1 bg-white border border-gray-300 rounded-md shadow-lg max-h-60 overflow-auto">
                        {getAvailableGifts().length === 0 ? (
                          <div className="px-3 py-2 text-gray-500 text-sm">
                            Eklenebilir hediye bulunamadı (Tüm hediye tipleri zaten mevcut)
                          </div>
                        ) : (
                          getAvailableGifts().map((gift) => (
                            <button
                              key={gift.id}
                              type="button"
                              onClick={() => handleGiftSelect(gift)}
                              className="w-full px-3 py-2 text-left hover:bg-gray-50 focus:bg-gray-50 focus:outline-none flex items-center space-x-3 border-b border-gray-100 last:border-b-0"
                            >
                              <img
                                src={gift.imageUrl}
                                alt={gift.name}
                                className="w-12 h-12 rounded-md object-cover flex-shrink-0"
                              />
                              <div className="flex-1 min-w-0">
                                <div className="text-sm font-medium text-gray-900 truncate">
                                  {gift.name}
                                </div>
                                <div className="text-sm text-blue-600 font-semibold">
                                  {gift.price}
                                </div>
                                {gift.isActive ? (
                                  <span className="inline-block px-2 py-1 text-xs font-medium bg-green-100 text-green-800 rounded-full">
                                    Aktif
                                  </span>
                                ) : (
                                  <span className="inline-block px-2 py-1 text-xs font-medium bg-red-100 text-red-800 rounded-full">
                                    Pasif
                                  </span>
                                )}
                              </div>
                            </button>
                          ))
                        )}
                      </div>
                    )}
                  </div>
                </div>

                {/* Hediye Resimleri */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Hediye Resimleri
                  </label>
                  
                  {/* Yüklenen Resimler */}
                  {giftImages.length > 0 && (
                    <div className="mb-4 grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
                      {giftImages.map((file, index) => (
                        <div key={index} className="relative group">
                          <div className="w-full h-32 bg-gray-100 border-2 border-gray-200 rounded-lg overflow-hidden shadow-sm">
                            <img
                              src={URL.createObjectURL(file)}
                              alt={`Gift ${index + 1}`}
                              className="w-full h-full object-cover"
                            />
                          </div>
                          <button
                            type="button"
                            onClick={() => removeGiftImage(index)}
                            className="absolute top-1 right-1 p-1 bg-red-500 text-white rounded-full opacity-0 group-hover:opacity-100 transition-opacity"
                          >
                            <X size={12} />
                          </button>
                        </div>
                      ))}
                    </div>
                  )}

                  {/* Resim Ekleme Butonu */}
                  <div className="border-2 border-dashed border-gray-300 rounded-lg p-6">
                    <input
                      type="file"
                      ref={giftImagesInputRef}
                      onChange={handleGiftImagesChange}
                      accept="image/*"
                      multiple
                      className="hidden"
                    />
                    <button
                      type="button"
                      onClick={() => giftImagesInputRef.current?.click()}
                      className="w-full flex flex-col items-center justify-center text-gray-500 hover:text-gray-700 transition-colors"
                    >
                      <Upload className="mb-2" size={24} />
                      <span className="text-sm">Hediye resimlerini seçin</span>
                      <span className="text-xs text-gray-400 mt-1">Birden fazla resim seçebilirsiniz</span>
                    </button>
                  </div>
                </div>

                {/* Hediye Ekleme Butonu */}
                <div className="flex justify-end">
                  <button
                    type="button"
                    onClick={handleGiftSubmit}
                    disabled={giftLoading || !selectedGiftId || giftImages.length === 0 || getAvailableGifts().length === 0}
                    className="flex items-center px-4 py-2 bg-purple-600 text-white rounded-md text-sm font-medium hover:bg-purple-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                  >
                    {giftLoading ? (
                      <>
                        <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin mr-2"></div>
                        Ekleniyor...
                      </>
                    ) : (
                      <>
                        <Gift className="mr-2" size={16} />
                        Hediye Ekle
                      </>
                    )}
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Mevcut Karakter Hediyeleri */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-4">Mevcut Hediyeler</label>
          
          {characterGiftsLoading ? (
            <div className="flex justify-center items-center py-8">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-emerald-600"></div>
            </div>
          ) : characterGifts.length === 0 ? (
            <div className="text-center py-8 text-gray-500">
              <Gift size={48} className="mx-auto mb-4 text-gray-300" />
              <p>Bu karakter için henüz hediye eklenmemiş</p>
            </div>
          ) : (
            <div className="space-y-6">
              {characterGifts.map((gift) => (
                <div key={gift.id} className="bg-white border border-gray-200 rounded-lg p-6 shadow-sm">
                  {/* Hediye Başlığı */}
                  <div className="flex items-center space-x-4 mb-4">
                    <img
                      src={gift.imageUrl}
                      alt={gift.name}
                      className="w-16 h-16 rounded-lg object-cover"
                    />
                    <div className="flex-1">
                      <h4 className="text-lg font-semibold text-gray-900">{gift.name}</h4>
                      <p className="text-blue-600 font-medium">{gift.price}</p>
                      <span className={`inline-block px-2 py-1 text-xs font-medium rounded-full ${
                        gift.isActive 
                          ? 'bg-green-100 text-green-800' 
                          : 'bg-red-100 text-red-800'
                      }`}>
                        {gift.isActive ? 'Aktif' : 'Pasif'}
                      </span>
                    </div>
                  </div>

                  {/* Karakter Hediye Varyantları */}
                  {gift.characterGifts.map((characterGift) => (
                    <div key={characterGift.id} className="border-t border-gray-100 pt-4">
                      <div className="flex items-center justify-between mb-3">
                        <h5 className="text-sm font-medium text-gray-700">
                          Hediye Varyantı #{characterGift.id}
                        </h5>
                        <div className="flex items-center space-x-2">
                          <span className={`inline-block px-2 py-1 text-xs font-medium rounded-full ${
                            characterGift.isAvailable 
                              ? 'bg-green-100 text-green-800' 
                              : 'bg-red-100 text-red-800'
                          }`}>
                            {characterGift.isAvailable ? 'Mevcut' : 'Mevcut Değil'}
                          </span>
                          <button
                            type="button"
                            onClick={() => {
                              const input = document.createElement('input');
                              input.type = 'file';
                              input.multiple = true;
                              input.accept = 'image/*';
                              input.onchange = (e) => {
                                const files = (e.target as HTMLInputElement).files;
                                if (files && files.length > 0) {
                                  updateGiftImages(characterGift.id, Array.from(files));
                                }
                              };
                              input.click();
                            }}
                            className="flex items-center px-2 py-1 bg-blue-600 text-white rounded text-xs hover:bg-blue-700 transition-colors"
                          >
                            <Upload size={12} className="mr-1" />
                            Güncelle
                          </button>
                        </div>
                      </div>
                      
                      {/* Hediye Resimleri */}
                      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-6 gap-3">
                        {characterGift.imageUrls.map((imageUrl, index) => (
                          <div key={index} className="relative group">
                            <img
                              src={imageUrl}
                              alt={`${gift.name} - Resim ${index + 1}`}
                              className="w-full h-24 object-cover rounded-md border border-gray-200 group-hover:shadow-md transition-shadow"
                            />
                            <div className="absolute inset-0 bg-black bg-opacity-0 group-hover:bg-opacity-10 rounded-md transition-opacity cursor-pointer"></div>
                            <button
                              type="button"
                              onClick={() => deleteGiftImage(characterGift.id, imageUrl)}
                              className="absolute top-1 right-1 p-1 bg-red-500 text-white rounded-full opacity-0 group-hover:opacity-100 transition-opacity hover:bg-red-600"
                            >
                              <Trash2 size={12} />
                            </button>
                          </div>
                        ))}
                      </div>
                    </div>
                  ))}
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Form Actions */}
        <div className="mt-8 flex justify-end space-x-4">
          <button
            type="button"
            onClick={() => navigate('/')}
            className="flex items-center px-4 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 hover:bg-gray-50"
          >
            <X className="mr-2" size={16} />
            İptal
          </button>
          <button
            type="submit"
            disabled={loading}
            className="flex items-center px-4 py-2 bg-emerald-600 text-white rounded-md text-sm font-medium hover:bg-emerald-700 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {loading ? (
              <>
                <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin mr-2"></div>
                Kaydediliyor...
              </>
            ) : (
              <>
                <Save className="mr-2" size={16} />
                Kaydet
              </>
            )}
          </button>
        </div>
      </form>

      {/* Delete Confirmation Modal */}
      {showDeleteConfirm && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 max-w-md w-full mx-4">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Karakteri Sil</h3>
            <p className="text-gray-600 mb-6">
              Bu karakteri silmek istediğinizden emin misiniz? Bu işlem geri alınamaz.
            </p>
            <div className="flex justify-end space-x-4">
              <button
                onClick={() => setShowDeleteConfirm(false)}
                className="px-4 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 hover:bg-gray-50"
              >
                İptal
              </button>
              <button
                onClick={handleDelete}
                disabled={loading}
                className="px-4 py-2 bg-red-600 text-white rounded-md text-sm font-medium hover:bg-red-700 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {loading ? 'Siliniyor...' : 'Sil'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default CharacterEdit; 